import { onAuthStateChanged, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { createFirebaseAuthDataSource } from '../../features/auth/data/firebaseAuthDataSource.js';
import { createAuthUseCases } from '../../features/auth/domain/authUseCases.js';
import { createAuthPresenter } from '../../features/auth/presenter/authPresenter.js';
import { createCatalogoRepository } from '../../features/comprador/data/catalogoRepository.js';
import { createPublicidadHomeRepository } from '../../features/comprador/data/publicidadHomeRepository.js';
import { createCalificacionesRepository } from '../../features/comprador/data/calificacionesRepository.js';
import { createNotificacionesRepository } from '../../features/perfil/data/notificacionesRepository.js';
import { createPerfilVentasRepository } from '../../features/comprador/data/perfilVentasRepository.js';
import { createDenunciasBarrioRepository } from '../../features/comprador/data/denunciasBarrioRepository.js';
import { createCatalogoPublicoPresenter } from '../../features/comprador/presenter/catalogoPublicoPresenter.js';
import { createPerfilVentasPresenter } from '../../features/comprador/presenter/perfilVentasPresenter.js';
import { createProductoFeedVerticalPresenter } from '../../features/comprador/presenter/productoFeedVerticalPresenter.js';
import { createAdminRepository } from '../../features/admin/data/adminRepository.js';
import { createAdminPresenter } from '../../features/admin/presenter/adminPresenter.js';
import { createVendedorRepository } from '../../features/vendedor/data/vendedorRepository.js';
import { createServiciosRepository } from '../../features/servicios/data/serviciosRepository.js';
import { createServiciosPresenters } from '../../features/servicios/presenter/serviciosPresenters.js';
import {
  isProfileComplete,
  perfilVendedorRequiereOnboarding,
} from '../../features/vendedor/domain/profileRules.js';
import { createVendedorPresenter } from '../../features/vendedor/presenter/vendedorPresenter.js';
import { createPerfilPresenter } from '../../features/perfil/presenter/perfilPresenter.js';
import { createPerfilScreen } from '../../features/perfil/presenter/perfilScreen.js';
import { createVendedorExperienciaInfoPresenter } from '../../features/perfil/presenter/vendedorExperienciaInfoPresenter.js';
import { createPremiumPlanPresenter } from '../../features/perfil/presenter/premiumPlanPresenter.js';
import {
  SS_GUEST_EXPLORE,
  SS_PENDING_PERFIL_VENDEDOR_UID,
  SS_PENDING_PRODUCT_OPEN,
} from '../sessionKeys.js';
import {
  normalizeMlvPerfilVendedorEntryFromLocation,
  normalizeMlvProductEntryFromLocation,
  parseMlvHashPerfilVendedorUid,
  parseMlvHashProductId,
  parseMlvHashServiciosRoute,
} from '../navigation/mlvDeepLink.js';
import { createMlvBrowserHistory } from '../navigation/mlvBrowserHistory.js';
import { createApplyMlvHistoryState } from '../navigation/applyMlvHistoryState.js';
import { createMlvNavigation } from '../navigation/mlvNavigation.js';
import { createWithFirestoreRetry } from '../firestore/withFirestoreRetry.js';
import { withTimeout } from '../network/timeout.js';
import { createShellViews } from '../ui/shellViews.js';
import { openNotificacionesResenaModal } from '../ui/notificacionesResenaModal.js';
import {
  applyMlvResenaNotifBadges,
  countPendientesResenaNotif,
} from '../ui/mlvResenaNotifBadges.js';
import { registerMlvServiceWorker } from './registerServiceWorker.js';
import { mlvScrollToTop } from '../navigation/mlvScrollTop.js';
import { runMlvExtensionsReady } from './mlvExtensions.js';
import './mlvFeatureRegistrations.js';
import { setMlvTelemetryUser } from '../telemetry/mlvTelemetryBootstrap.js';

let mlvDeferredInstallPrompt = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    mlvDeferredInstallPrompt = e;
  });
}

/**
 * Compone repositorios, presenters, historial y navegación; arranca auth listener.
 * @param {object} options
 * @param {HTMLElement} options.appEl
 * @param {() => object} options.getFirebase
 */
export function bootstrapMlvApp({ appEl, getFirebase }) {
  if (typeof window !== 'undefined') {
    normalizeMlvProductEntryFromLocation();
    normalizeMlvPerfilVendedorEntryFromLocation();
  }

  const render = (html) => {
    appEl.innerHTML = html;
  };

  let loginView = () => {};

  const shell = createShellViews({
    render,
    getLoginView: () => loginView,
  });

  const withFirestoreRetry = createWithFirestoreRetry({ getFirebase });

  /* ---------- Repos (Firestore / APIs) ---------- */
  const authDataSource = createFirebaseAuthDataSource();
  const authUseCases = createAuthUseCases({ getFirebase, dataSource: authDataSource });
  const catalogoRepository = createCatalogoRepository({ getFirebase });
  const publicidadHomeRepository = createPublicidadHomeRepository({ getFirebase });
  const calificacionesRepository = createCalificacionesRepository({ getFirebase });
  const notificacionesRepository = createNotificacionesRepository({ getFirebase });
  const denunciasBarrioRepository = createDenunciasBarrioRepository({ getFirebase });

  let lastResenaNotifItems = [];
  let resenaNotifUnsub = null;

  function tearDownResenaNotifListening() {
    if (typeof resenaNotifUnsub === 'function') {
      resenaNotifUnsub();
      resenaNotifUnsub = null;
    }
    lastResenaNotifItems = [];
    applyMlvResenaNotifBadges(0);
  }

  function setupResenaNotifListening(uid) {
    tearDownResenaNotifListening();
    if (!uid || !notificacionesRepository) return;
    resenaNotifUnsub = notificacionesRepository.subscribeNotificaciones(
      uid,
      (items) => {
        lastResenaNotifItems = items;
        applyMlvResenaNotifBadges(countPendientesResenaNotif(items));
      },
      (e) => console.error(e)
    );
  }

  async function openResenaNotificacionesModalApp() {
    const u = getFirebase().auth.currentUser;
    if (!u) return;
    await openNotificacionesResenaModal({
      items: [...lastResenaNotifItems],
      calificacionesRepository,
      invalidateCatalogoCache: () => catalogoRepository?.invalidateCatalogoCache?.(),
      marcarLeida: (uid, nid) => notificacionesRepository?.marcarLeida(uid, nid),
      uidComprador: u.uid,
      ejecutarValidacionUbicacionGps: () => denunciasBarrioRepository.ejecutarValidacionUbicacionGps(),
    });
  }

  const productoFeedVerticalPresenter = createProductoFeedVerticalPresenter({
    render,
    catalogoRepository,
  });
  const perfilVentasRepository = createPerfilVentasRepository({ getFirebase });
  const perfilVentasPresenter = createPerfilVentasPresenter({
    render,
    perfilVentasRepository,
    catalogoRepository,
    getFirebase,
    denunciasBarrioRepository,
  });
  const adminRepository = createAdminRepository({ getFirebase });
  const perfilPresenter = createPerfilPresenter({ render });
  const vendedorExperienciaInfoPresenter = createVendedorExperienciaInfoPresenter({ render });
  const premiumPlanPresenter = createPremiumPlanPresenter({ render });
  const adminPresenter = createAdminPresenter({ render, adminRepository });
  const vendedorRepository = createVendedorRepository({ getFirebase });
  const navHolder = {};
  const serviciosRepository = createServiciosRepository({ getFirebase });
  const serviciosPresenters = createServiciosPresenters({
    render,
    getFirebase,
    serviciosRepository,
    vendedorRepository,
    getNavigation: () => navHolder.navigation,
  });

  const appCtx = { navigation: null };

  /* ---------- Presenters (pantallas) ---------- */
  const vendedorPresenter = createVendedorPresenter({
    render,
    getFirebase,
    repo: vendedorRepository,
    onBackHome: () => appCtx.navigation.replaceCatalog(),
    onOpenProfile: () => appCtx.navigation.goToProfile(),
    onIrPlanPremium: () => void appCtx.navigation.pushPremiumPlan(),
    onPerfilVendedorCompleto: () => appCtx.navigation.replaceCatalog(),
    onIrAlCatalogo: () => appCtx.navigation.replaceCatalog(),
    onOpenServicios: () => void appCtx.navigation.pushServiciosHome(),
    isProfileComplete,
    onMisNavPerfil: () => appCtx.navigation.handleMisBottomNavPerfil(),
    onBeforeOpenPublicarDesdeMis: () => appCtx.navigation.goToVendedorPublicar(),
    onPubMisPushHistorial: () => appCtx.navigation.pushVendedorMis(),
    onPubMisCerrarHistorial: () => appCtx.navigation.goBack(),
  });

  const catalogoPublicoPresenter = createCatalogoPublicoPresenter({
    render,
    getFirebase,
    catalogoRepository,
    publicidadHomeRepository,
    perfilVentasRepository,
    loadUserProfileForWa: (uid) =>
      vendedorRepository.syncUserProfile(uid, getFirebase().auth.currentUser?.email || null),
    onBackToLogin: () => appCtx.navigation.pushLoginShow(),
    onOpenProfile: () => appCtx.navigation.goToProfile(),
    onSellProduct: () => appCtx.navigation.openSellOrOnboarding(),
    onOpenProductoDetalle: (id) => appCtx.navigation.pushProductDetail(id),
    onOpenPerfilVendedorUid: (uid) => appCtx.navigation.pushPerfilVendedorUid(uid),
    onOpenNotificacionesResena: () => void openResenaNotificacionesModalApp(),
    onOpenServiciosBarrio: () => {
      if (!getFirebase().auth.currentUser) appCtx.navigation.pushLoginShow();
      else appCtx.navigation.pushServiciosHome();
    },
    vendedorRepository,
    adminRepository,
  });

  function showCatalogoHomePlain() {
    mlvScrollToTop();
    const u = getFirebase().auth.currentUser;
    let guestExplore = false;
    try {
      guestExplore = sessionStorage.getItem(SS_GUEST_EXPLORE) === '1';
    } catch (_e) {
      guestExplore = false;
    }
    catalogoPublicoPresenter.showCatalogoPublico({
      isAuthenticated: Boolean(u) && !guestExplore,
      currentUser: guestExplore ? null : u,
      syncResenaNotifBadges: u
        ? () =>
            applyMlvResenaNotifBadges(countPendientesResenaNotif(lastResenaNotifItems))
        : undefined,
    });
  }

  async function showAdminPanelView() {
    const user = getFirebase().auth.currentUser;
    if (!user) {
      loginView();
      return;
    }
    const idTokenResult = await user.getIdTokenResult(true);
    if (!idTokenResult.claims.admin) {
      appCtx.navigation.replaceCatalog();
      return;
    }
    await adminPresenter.showPanelAdmin({
      onBack: () => appCtx.navigation.goBack(),
    });
  }

  const { renderProfileView, showProfileView } = createPerfilScreen({
    getFirebase,
    perfilPresenter,
    vendedorRepository,
    isProfileComplete,
    vendedorPresenter,
    appCtx,
    getLoginView: () => loginView,
    calificacionesRepository,
    notificacionesRepository,
    catalogoRepository,
    getNotificacionesItems: () => [...lastResenaNotifItems],
    getDeferredInstallPrompt: () => mlvDeferredInstallPrompt,
    clearDeferredInstallPrompt: () => {
      mlvDeferredInstallPrompt = null;
    },
    ejecutarValidacionUbicacionGps: () => denunciasBarrioRepository.ejecutarValidacionUbicacionGps(),
  });

  let navigation;

  /* ---------- Historial + navegación ---------- */
  const applyMlvHistoryState = createApplyMlvHistoryState({
    getFirebase,
    get loginView() {
      return loginView;
    },
    showCatalogoHomePlain,
    renderProfileView,
    showAdminPanelView,
    catalogoPublicoPresenter,
    perfilVentasPresenter,
    productoFeedVerticalPresenter,
    vendedorPresenter,
    vendedorRepository,
    vendedorExperienciaInfoPresenter,
    premiumPlanPresenter,
    isProfileComplete,
    get navigation() {
      return navigation;
    },
    serviciosPresenters,
  });

  const mlvHist = createMlvBrowserHistory({
    applyState: applyMlvHistoryState,
  });

  navigation = createMlvNavigation({
    getFirebase,
    mlvHist,
    catalogoPublicoPresenter,
    perfilVentasPresenter,
    productoFeedVerticalPresenter,
    vendedorPresenter,
    vendedorRepository,
    vendedorExperienciaInfoPresenter,
    premiumPlanPresenter,
    showCatalogoHomePlain,
    getLoginView: () => loginView,
    showProfileView,
    showAdminPanelView,
    isProfileComplete,
    serviciosPresenters,
  });

  appCtx.navigation = navigation;
  navHolder.navigation = navigation;

  /* ---------- Auth (login Google) ---------- */
  ({ loginView } = createAuthPresenter({
    render,
    authUseCases,
    onLoginSuccess: () => {
      try {
        sessionStorage.removeItem(SS_GUEST_EXPLORE);
      } catch (_e) {
        /* ignore */
      }
      // Tras login: routeUser corre solo desde onAuthStateChanged.
    },
    onGuestAccess: () => {
      const pid = parseMlvHashProductId();
      const pvu = parseMlvHashPerfilVendedorUid();
      navigation.pushCatalogGuestEntry();
      if (pid) navigation.pushProductDetail(pid);
      else if (pvu) navigation.pushPerfilVendedorUid(pvu);
    },
  }));

  /* ---------- Extensiones (features nuevas; ver mlvFeatureRegistrations.js) ---------- */
  runMlvExtensionsReady({
    getFirebase,
    render,
    appEl,
    getNavigation: () => navigation,
    catalogoRepository,
    publicidadHomeRepository,
    perfilVentasRepository,
    calificacionesRepository,
    notificacionesRepository,
    adminRepository,
    vendedorRepository,
    catalogoPublicoPresenter,
    perfilVentasPresenter,
    productoFeedVerticalPresenter,
    vendedorPresenter,
    adminPresenter,
    premiumPlanPresenter,
    perfilPresenter,
    vendedorExperienciaInfoPresenter,
    authUseCases,
    getLoginView: () => loginView,
    mlvBrowserHistory: mlvHist,
  });

  /**
   * Allowlist server-side: el primer login del email admin recibe el claim `admin: true`.
   * Idempotente: si ya tiene el claim, no hace nada.
   */
  async function intentarAutoAsignarAdminClaim(user) {
    if (!user) return false;
    try {
      const tr = await user.getIdTokenResult();
      if (tr?.claims?.admin === true) return false;
    } catch (_e) {
      /* sigue */
    }
    try {
      const { fns } = getFirebase();
      if (!fns) return false;
      const call = httpsCallable(fns, 'verificarAdminClaim');
      const res = await call({});
      const granted = res?.data?.granted === true;
      if (granted) {
        // Forzamos refresh del token para que el cliente vea el claim al instante.
        await user.getIdToken(true);
      }
      return granted;
    } catch (_e) {
      return false;
    }
  }

  /* ---------- Ruteo post-login ---------- */
  async function routeUser(user) {
    if (!user) {
      const pendProduct = parseMlvHashProductId();
      if (pendProduct) {
        try {
          sessionStorage.setItem(SS_PENDING_PRODUCT_OPEN, pendProduct);
        } catch (_e) {
          /* ignore */
        }
      }
      const pendPvu = parseMlvHashPerfilVendedorUid();
      if (pendPvu) {
        try {
          sessionStorage.setItem(SS_PENDING_PERFIL_VENDEDOR_UID, pendPvu);
        } catch (_e) {
          /* ignore */
        }
      }
      mlvScrollToTop();
      mlvHist.replace({ t: 'login' });
      return loginView();
    }

    try {
      sessionStorage.removeItem(SS_GUEST_EXPLORE);
    } catch (_e) {
      /* ignore */
    }

    mlvHist.reconcileStateWithHash();

    shell.loadingView();
    const profile = await withFirestoreRetry(() =>
      vendedorRepository.syncUserProfile(user.uid, user.email)
    );

    // Auto-asignar admin claim (no bloquear ingreso si Functions va lento o falla).
    try {
      await withTimeout(intentarAutoAsignarAdminClaim(user), 8000, 'Claim admin: sin respuesta.');
    } catch (_e) {
      /* ignore */
    }

    let esAdminClaim = false;
    try {
      const tr = await user.getIdTokenResult();
      esAdminClaim = tr?.claims?.admin === true;
    } catch (_e) {
      esAdminClaim = false;
    }
    if (!esAdminClaim && String(profile?.estadoCuenta || '').toLowerCase() === 'suspendida') {
      mlvHist.replace({ t: 'login' });
      shell.cuentaSuspendidaView({
        suspensionMotivo: profile?.suspension_motivo || null,
        onCerrarSesion: () => signOut(getFirebase().auth),
        onValidarUbicacion:
          String(profile?.suspension_motivo || '') === 'ubicacion_gps_pendiente'
            ? () => denunciasBarrioRepository.ejecutarValidacionUbicacionGps()
            : null,
      });
      return;
    }

    const fromHashPid = parseMlvHashProductId();
    const fromHashPvu = parseMlvHashPerfilVendedorUid();

    let deepPid = fromHashPid;
    if (!deepPid) {
      try {
        deepPid = sessionStorage.getItem(SS_PENDING_PRODUCT_OPEN);
        if (deepPid) sessionStorage.removeItem(SS_PENDING_PRODUCT_OPEN);
      } catch (_e) {
        deepPid = null;
      }
    }
    let deepPvu = fromHashPvu;
    if (!deepPvu) {
      try {
        deepPvu = sessionStorage.getItem(SS_PENDING_PERFIL_VENDEDOR_UID);
        if (deepPvu) sessionStorage.removeItem(SS_PENDING_PERFIL_VENDEDOR_UID);
      } catch (_e) {
        deepPvu = null;
      }
    }

    if (deepPid) {
      mlvHist.replace({ t: 'product', id: deepPid });
      await catalogoPublicoPresenter.showProductoDetalle(
        deepPid,
        navigation.buildProductoDetalleOpts()
      );
      return;
    }
    if (deepPvu && /^[A-Za-z0-9_-]{10,128}$/.test(String(deepPvu).trim())) {
      const u = String(deepPvu).trim();
      mlvHist.replace({ t: 'perfilVendedor', uid: u });
      await perfilVentasPresenter.show({
        vendedorUid: u,
        onBack: () => navigation.goBack(),
        onOpenProducto: (id) => navigation.pushProductDetail(id),
      });
      return;
    }

    const deepSrv = parseMlvHashServiciosRoute();
    if (deepSrv) {
      mlvHist.replace(deepSrv);
      if (deepSrv.mode === 'miPerfil') await serviciosPresenters.showServiciosMiPerfil();
      else if (deepSrv.mode === 'ficha' && deepSrv.uid) {
        await serviciosPresenters.showServiciosFicha(String(deepSrv.uid).trim());
      } else await serviciosPresenters.showServiciosHome();
      return;
    }

    if (perfilVendedorRequiereOnboarding(profile) && !isProfileComplete(profile)) {
      mlvScrollToTop();
      mlvHist.replace({ t: 'vendedor', mode: 'datos' });
      vendedorPresenter.datosVendedorView(profile);
      return;
    }

    mlvHist.replace({ t: 'catalog' });
    return showCatalogoHomePlain();
  }

  registerMlvServiceWorker();
  shell.loadingView();
  onAuthStateChanged(getFirebase().auth, (user) => {
    if (user?.uid) {
      setupResenaNotifListening(user.uid);
      setMlvTelemetryUser(user.uid);
    } else {
      tearDownResenaNotifListening();
      setMlvTelemetryUser(null);
    }
    const p = routeUser(user);
    if (p && typeof p.catch === 'function') {
      p.catch((err) => {
        console.error(err);
        shell.errorView('Error al cargar tu cuenta', err);
      });
    }
  });
}
